// components/card/card-component/CardStatusBadges.tsx
import { memo, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Typography } from "../../Typography";
import { IconBolt } from "@tabler/icons-react";

export type CardStatusBadgesProps = {
  disPercent?: number | null;
  availMsg?: string | number | null;
  isInstantConfirm?: boolean;
  className?: string;
  shadow?: boolean;
  badgeClassNames?: Partial<
    Record<"discount" | "availMsg" | "instantConfirm", string>
  >;
};

const BADGE_BASE_CLASS =
  "flex items-center gap-0.5 w-fit max-w-full overflow-hidden text-nowrap text-ellipsis rounded-lg px-2 py-1";

type BadgeConfig = {
  key: "discount" | "availMsg" | "instantConfirm";
  show: boolean;
  colorClass: string;
  content: ReactNode;
};

const CardStatusBadges = ({
  disPercent,
  availMsg,
  isInstantConfirm,
  className,
  shadow = true,
  badgeClassNames,
}: CardStatusBadgesProps) => {
  const badges: BadgeConfig[] = [
    {
      key: "discount",
      show: Boolean(disPercent && disPercent > 0),
      colorClass: "bg-success-background text-success-foreground",
      content: `${disPercent}% OFF`,
    },
    {
      key: "availMsg",
      show: Boolean(availMsg),
      colorClass: "bg-error-background text-error-foreground",
      content: `Only ${availMsg} left`,
    },
    {
      key: "instantConfirm",
      show: Boolean(isInstantConfirm),
      colorClass: "bg-success-background text-success-foreground",
      content: (
        <>
          <IconBolt className="size-4 shrink-0" stroke={1.5} />
          <span>Instant Confirmation</span>
        </>
      ),
    },
  ];

  const visibleBadges = badges.filter((badge) => badge.show);

  if (visibleBadges.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex flex-col w-fit gap-1", className)}>
      {visibleBadges.map((badge) => (
        <Typography
          key={badge.key}
          variant="body-xs"
          className={cn(
            BADGE_BASE_CLASS,
            badge.colorClass,
            shadow && "shadow-md",
            badgeClassNames?.[badge.key],
          )}
        >
          {badge.content}
        </Typography>
      ))}
    </div>
  );
};

export default memo(CardStatusBadges);
